/**
 * Stat highlight card (acf.statcard).
 * Maps: label → circle prefix, figure → main stat, body → description, circlecolor → circle fill.
 */

import { OpsiHtmlContent } from "../utils/OpsiHtmlContent";
import { hasHtmlContent, OPSI_PART_SPACING } from "../utils/opsiHelpers";

const DEFAULT_CIRCLE_COLOR = "#993189";
const DEFAULT_PILL_BACKGROUND = "#fbe8f2";

/** Plain-text fields from ACF are usually strings; body may contain <p> markup. */
const toDisplayText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();

const pillBackgroundFromCircle = (circleColor) => {
  if (!circleColor) {
    return DEFAULT_PILL_BACKGROUND;
  }

  return `color-mix(in srgb, ${circleColor} 14%, white)`;
};

export const OpsiStatCard = ({ data, className = "opsi-stat-card" }) => {
  if (!data) {
    return null;
  }

  const { label, figure, body, circlecolor } = data;
  const circleColor = circlecolor || DEFAULT_CIRCLE_COLOR;
  const labelText = toDisplayText(label);
  const figureText = toDisplayText(figure);
  const hasBody = hasHtmlContent(body);

  if (!labelText && !figureText && !hasBody) {
    return null;
  }

  return (
    <section
      // Internal px on the pill — unlike other OPSI sections, which rely on the parent container.
      className={`${className} ${OPSI_PART_SPACING} flex w-full items-center gap-5 rounded-[999px] px-5 py-5 md:gap-8 md:px-10 md:py-7`}
      data-opsi-part="statcard"
      style={{ backgroundColor: pillBackgroundFromCircle(circleColor) }}
    >
      {(labelText || figureText) && (
        <div
          className="flex h-[130px] w-[130px] shrink-0 flex-col items-center justify-center rounded-full px-3 text-center text-white md:h-[155px] md:w-[155px]"
          style={{ backgroundColor: circleColor }}
          aria-label={[labelText, figureText].filter(Boolean).join(" ")}
        >
          {labelText ? (
            <span className="text-[0.95rem] font-normal leading-tight md:text-[1.05rem]">
              {labelText}
            </span>
          ) : null}
          {figureText ? (
            <span className="mt-0.5 text-[2.75rem] font-bold leading-none md:text-[3.25rem]">
              {figureText}
            </span>
          ) : null}
        </div>
      )}

      {hasBody ? (
        <div className="opsi-stat-card__body min-w-0 flex-1 text-[1rem] font-medium leading-snug text-[#1a1a1a] md:text-[1.125rem] [&_p]:m-0">
          <OpsiHtmlContent html={body} />
        </div>
      ) : null}
    </section>
  );
};
